
import {computed, defineComponent, reactive, toRefs} from 'vue';
import {Position, COLOR_DROPDOWN_POSITIONS, LEFT, RIGHT} from '../types';
import clickOutsideDirective from '../../../../directives/click-outside';
import ColorPicker from '@ohrm/oxd/core/components/Input/Color/ColorPicker.vue';

export default defineComponent({
  name: 'oxd-color-input',
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    dropdownPosition: {
      type: String,
      default: RIGHT,
      validator: (value: Position) =>
        COLOR_DROPDOWN_POSITIONS.indexOf(value) !== -1,
    },
  },

  emits: ['update:modelValue'],

  directives: {
    'click-outside': clickOutsideDirective,
  },

  components: {
    'oxd-color-picker': ColorPicker,
  },

  setup(props) {
    const state = reactive({
      open: false,
      focused: false,
    });
    const previewStyles = computed(() => ({
      backgroundColor: props.modelValue ?? '#000',
      opacity: props.disabled || props.readonly ? 0.5 : 1,
      cursor: props.disabled || props.readonly ? 'unset' : 'pointer',
    }));

    const classes = computed(() => ({
      'oxd-color-input': true,
      'oxd-color-input--active': !state.focused,
      'oxd-color-input--focus': state.focused || state.open,
      'oxd-color-input--error': props.hasError,
      'oxd-color-input--disabled': props.disabled,
      'oxd-color-input--readonly': props.readonly,
    }));

    const dropdownClasses = computed(() => ({
      '--positon-right': props.dropdownPosition === RIGHT,
      '--positon-left': props.dropdownPosition === LEFT,
    }));

    const tabIndex = computed(() => {
      return props.disabled || props.readonly ? -1 : 0;
    });

    const onFocus = ($e: Event) => {
      if (props.disabled || props.readonly) {
        $e.stopImmediatePropagation();
        return;
      }
      state.focused = true;
    };

    const onBlur = () => {
      state.focused = false;
    };

    const onClose = () => {
      state.open = false;
    };

    const togglePicker = ($e: Event) => {
      if (props.disabled || props.readonly) {
        $e.stopImmediatePropagation();
        return;
      }
      state.open = !state.open;
    };

    return {
      tabIndex,
      classes,
      previewStyles,
      dropdownClasses,
      onBlur,
      onFocus,
      onClose,
      onClick: togglePicker,
      onEnter: togglePicker,
      ...toRefs(state),
    };
  },
});
